// Table Types and Requirements System

export enum TableType {
  CUSTOM = 'custom'
}

export interface TableTypeInfo {
  id: TableType;
  name: string;
  description: string;
  icon: string; // Heroicon name
  category: 'workflow' | 'analysis';
  requiredTemplates: string[]; // Template names that must exist
  suggestedColumns: TableColumnTemplate[];
  contextRequirements: string[]; // What context is needed before table can run
}

export interface TableColumnTemplate {
  name: string;
  type: 'text' | 'number' | 'date' | 'status' | 'platform_data' | 'communication';
  platform_resources?: string[]; // For platform-specific columns
  required?: boolean;
  description?: string;
  requirement_config?: {
    template_ref?: string; // Reference to template if applicable
    copyable?: boolean; // Whether cell content should be easily copyable
    required?: boolean; // Whether this requirement is mandatory
    placeholder?: string; // Placeholder text for empty cells
  };
}

export interface TableRequirement {
  id: string; // Unique identifier for the requirement
  type: 'template' | 'context' | 'custom';
  name: string;
  description: string;
  satisfied: boolean;
  createdAt?: string; // For custom requirements
  updatedAt?: string;
}

export interface CustomTableRequirement {
  id: string;
  tableId: string;
  type: 'template' | 'context' | 'custom';
  name: string;
  description: string;
  satisfied: boolean;
  createdAt: string;
  updatedAt: string;
}

export const TABLE_TYPES: Record<TableType, TableTypeInfo> = {
  [TableType.CUSTOM]: {
    id: TableType.CUSTOM,
    name: 'Custom Table',
    description: 'Empty table where you build everything from scratch',
    icon: 'PlusIcon',
    category: 'analysis',
    requiredTemplates: [],
    suggestedColumns: [],
    contextRequirements: []
  }
};

export const getTableTypesByCategory = (category: 'workflow' | 'analysis') => {
  return Object.values(TABLE_TYPES).filter(type => type.category === category);
};

// Helper function to safely convert string to TableType enum
export const parseTableType = (tableTypeString: string | undefined): TableType => {
  if (!tableTypeString) return TableType.CUSTOM;
  
  // Check if the string matches any of the enum values
  const validTableType = Object.values(TableType).find(type => type === tableTypeString);
  return validTableType || TableType.CUSTOM;
};

export const getTableTypeRequirements = (tableType: TableType, existingTemplates: string[]): TableRequirement[] => {
  const typeInfo = TABLE_TYPES[tableType];
  
  // Return empty array if table type is not found
  if (!typeInfo) {
    console.warn(`Table type "${tableType}" not found in TABLE_TYPES`);
    return [];
  }
  
  const requirements: TableRequirement[] = [];
  
  // Check template requirements
  typeInfo.requiredTemplates.forEach((templateName, index) => {
    requirements.push({
      id: `template-${templateName.toLowerCase().replace(/\s+/g, '-')}`,
      type: 'template',
      name: templateName,
      description: `${templateName} template must be created`,
      satisfied: existingTemplates.includes(templateName)
    });
  });
  
  // Add context requirements (these need to be checked separately)
  typeInfo.contextRequirements.forEach((requirement, index) => {
    requirements.push({
      id: `context-${index}`,
      type: 'context',
      name: requirement,
      description: requirement,
      satisfied: false // Will be determined by business logic
    });
  });
  
  return requirements;
};

// New function to get all requirements for a table (predefined + custom)
export const getAllTableRequirements = (
  tableType: TableType, 
  existingTemplates: string[], 
  customRequirements: CustomTableRequirement[] = []
): TableRequirement[] => {
  // Ensure tableType is valid, fallback to CUSTOM if not
  const validTableType = Object.values(TableType).includes(tableType) ? tableType : TableType.CUSTOM;
  
  const predefinedRequirements = getTableTypeRequirements(validTableType, existingTemplates);
  
  // Convert custom requirements to TableRequirement format
  const customReqs: TableRequirement[] = customRequirements.map(req => ({
    id: req.id,
    type: req.type,
    name: req.name,
    description: req.description,
    satisfied: req.satisfied,
    createdAt: req.createdAt,
    updatedAt: req.updatedAt
  }));
  
  return [...predefinedRequirements, ...customReqs];
};

export const isTableReadyToRun = (tableType: TableType, existingTemplates: string[]): boolean => {
  const requirements = getTableTypeRequirements(tableType, existingTemplates);
  const templateRequirements = requirements.filter(req => req.type === 'template');
  
  // For now, only check template requirements
  // Context requirements will be added later
  return templateRequirements.every(req => req.satisfied);
};

export const canCreateTable = (tableType: TableType, existingTemplates: string[]): boolean => {
  // Always allow table creation - requirements can be fixed after creation
  return true;
};
